<template>
  <UCard
    :as="as"
    :title="title"
    :description="description"
    :variant="variant"
    :ui="ui"
    :data-size="size"
    class="p-card group/p-card divide-gray-lighten-2 dark:divide-gray-lighten-2-dark min-w-0 divide-y overflow-hidden rounded-md"
    :class="[
      colorClass,
      {
        'hover:bg-primary-lighten-1/50 hover:text-on-primary-lighten-1 dark:hover:bg-primary-lighten-1-dark/50 dark:hover:text-on-primary-lighten-1-dark cursor-pointer':
          hoverable,
        'hover:[&_:is(.text-gray,.text-gray-lighten-1,.text-gray-lighten-2)]:text-on-primary-lighten-1! dark:hover:[&_:is(.text-gray,.text-gray-lighten-1,.text-gray-lighten-2)]:text-on-primary-lighten-1-dark!':
          hoverable
      }
    ]"
  >
    <template v-if="$slots.header" #header><slot name="header" /></template>
    <template v-if="$slots.title" #title><slot name="title" /></template>
    <template v-if="$slots.description" #description><slot name="description" /></template>
    <template v-if="$slots.default" #default><slot /></template>
    <template v-if="$slots.footer" #footer><slot name="footer" /></template>
  </UCard>
</template>

<script setup lang="ts">
import { computed, type VNode } from "vue"
import UCard, { type CardProps } from "@nuxt/ui/components/Card.vue"
import type { ComponentColor } from "@/config/component-colors"

const props = withDefaults(
  defineProps<{
    /** Native element used for the card container. */
    as?: "div" | "article" | "section" | "li"
    title?: string
    description?: string
    /** Background and border treatment. */
    variant?: "outline" | "solid" | "soft" | "subtle"
    /** Card color; defaults to the Peanuts gray palette. */
    color?: ComponentColor
    /** Use sm for compact, collapsed list items. */
    size?: "sm" | "md" | "lg"
    /** Highlight the card on hover; disable for inaccessible items. */
    hoverable?: boolean
    /** Customize the underlying card slots. */
    ui?: CardProps["ui"]
  }>(),
  { as: "div", variant: "outline", color: "gray-lighten-2", size: "md", hoverable: true }
)

defineSlots<{
  header?(): VNode[]
  title?(): VNode[]
  description?(): VNode[]
  default?(): VNode[]
  footer?(): VNode[]
}>()

const appearances = {
  "gray-lighten-1": {
    border: "border-gray-lighten-1 dark:border-gray-lighten-1-dark",
    soft: "bg-gray-lighten-1 dark:bg-gray-lighten-1-dark",
    solid: "gray-lighten-1"
  },
  "gray-lighten-2": {
    border: "border-gray-lighten-2 dark:border-gray-lighten-2-dark",
    soft: "bg-gray-lighten-2 dark:bg-gray-lighten-2-dark",
    solid: "gray-lighten-2"
  },
  primary: {
    border: "border-primary dark:border-primary-dark",
    soft: "bg-primary-lighten-1/10 dark:bg-primary-lighten-1-dark/10",
    solid: "primary"
  },
  "primary-lighten-1": {
    border: "border-primary-lighten-1 dark:border-primary-lighten-1-dark",
    soft: "bg-primary-lighten-1/10 dark:bg-primary-lighten-1-dark/10",
    solid: "primary-lighten-1"
  },
  surface: {
    border: "border-surface dark:border-surface-dark",
    soft: "bg-surface dark:bg-surface-dark",
    solid: "surface"
  },
  secondary: {
    border: "border-secondary dark:border-secondary-dark",
    soft: "bg-secondary/10 dark:bg-secondary-dark/10",
    solid: "secondary"
  },
  info: {
    border: "border-info-lighten-1 dark:border-info-lighten-1-dark",
    soft: "bg-info-lighten-2 dark:bg-info-lighten-2-dark",
    solid: "bg-info text-on-info dark:bg-info-dark dark:text-on-info-dark"
  },
  success: {
    border: "border-success-lighten-1 dark:border-success-lighten-1-dark",
    soft: "bg-success-lighten-2 dark:bg-success-lighten-2-dark",
    solid: "bg-success text-on-success dark:bg-success-dark dark:text-on-success-dark"
  },
  warning: {
    border: "border-warning-lighten-1 dark:border-warning-lighten-1-dark",
    soft: "bg-warning-lighten-2 dark:bg-warning-lighten-2-dark",
    solid: "bg-warning text-on-warning dark:bg-warning-dark dark:text-on-warning-dark"
  },
  error: {
    border: "border-error-lighten-1 dark:border-error-lighten-1-dark",
    soft: "bg-error-lighten-2 dark:bg-error-lighten-2-dark",
    solid: "bg-error text-on-error dark:bg-error-dark dark:text-on-error-dark"
  }
} as const satisfies Record<ComponentColor, { border: string; soft: string; solid: string }>

const colorClass = computed(() => {
  const { border, soft, solid } = appearances[props.color]
  const text = "text-on-surface dark:text-on-surface-dark"
  switch (props.variant) {
    case "solid":
      return solid
    case "soft":
      return [text, soft]
    case "subtle":
      return [text, soft, "border-3", border]
    default:
      return [text, "bg-surface dark:bg-surface-dark border-3", border]
  }
})
</script>
